// App Type Registry
// Single source of truth for Revolt app types (browser, local, website, desktop, ...).
// Used across the site for:
//   - Create-app UI (App Type picker, template filtering)
//   - Access gating (AppCard Workspace/Playspace buttons, route guards)
//   - Workspace/playspace runtime branching (alongside app.appType on the record)
//   - AI system prompt extensions (appendAppTypeExtensions)
//
// Keep this file framework-free so it can be imported from any section.
// The authoritative appType for a given app still lives on the Mongo app record
// (app.appType). This registry describes what each type MEANS at the UI/client layer.

/**
 * @typedef {Object} AppTypeDefinition
 * @property {string} id                  - Lowercase identifier stored on app.appType
 * @property {string} displayName         - Human-readable label ("Browser App")
 * @property {string} shortName           - Compact label for badges ("Browser")
 * @property {string} description         - One-line description for pickers
 * @property {string} icon                - Font Awesome class (no leading 'fa-')
 * @property {"any"|"desktop"} runEnvironment    - Where this app can actually run/open
 * @property {"any"|"desktop"} createEnvironment - Where the app can be CREATED from
 * @property {Object} capabilities
 * @property {boolean} capabilities.workspace    - Has a Workspace (IDE) experience
 * @property {boolean} capabilities.playspace    - Has a Playspace (preview) experience
 * @property {"browser"|"remote"|"native"|"none"} capabilities.previewMode
 * @property {boolean} capabilities.cloudFiles   - Uses DO Spaces file bundles
 * @property {boolean} capabilities.browserGit   - Uses isomorphic-git in LightningFS
 * @property {boolean} capabilities.nativeGit    - Uses real git CLI (desktop IPC)
 * @property {boolean} enabled            - Whether user can pick this when creating
 * @property {boolean} experimental       - Flag it in UI as early-access
 */

/** @type {AppTypeDefinition[]} */
export const APP_TYPES = [
  {
    id: "browser",
    displayName: "Browser App",
    shortName: "Browser",
    description: "Runs entirely in the browser. Files are stored in the cloud and synced via LightningFS + isomorphic-git.",
    icon: "globe",
    runEnvironment: "any",
    createEnvironment: "any",
    capabilities: {
      workspace: true,
      playspace: true,
      previewMode: "browser",
      cloudFiles: true,
      browserGit: true,
      nativeGit: false,
    },
    enabled: true,
    experimental: false,
  },
  {
    id: "local",
    displayName: "Local App",
    shortName: "Local",
    description: "Opens a working directory on your computer. Requires Revolt Desktop. Uses your real filesystem and git.",
    icon: "folder-tree",
    runEnvironment: "desktop",
    // Creation requires Revolt Desktop because the user has to pick a native folder.
    createEnvironment: "desktop",
    capabilities: {
      workspace: true,
      playspace: false,
      previewMode: "none",
      cloudFiles: false,
      browserGit: false,
      nativeGit: true,
    },
    enabled: true,
    experimental: false,
  },
];

const DEFAULT_APP_TYPE_ID = "browser";

const APP_TYPES_BY_ID = APP_TYPES.reduce((acc, t) => {
  acc[t.id] = t;
  return acc;
}, {});

/**
 * Normalize any appType-ish value to a lowercase string.
 * @param {unknown} appType
 * @returns {string}
 */
export function normalizeAppType(appType) {
  return typeof appType === "string" ? appType.toLowerCase() : "";
}

/**
 * Look up a registry entry by id. Returns null for unknown ids.
 * @param {string} appType
 * @returns {AppTypeDefinition|null}
 */
export function getAppType(appType) {
  return APP_TYPES_BY_ID[normalizeAppType(appType)] || null;
}

/**
 * Return the default app type definition (used as fallback in pickers).
 * @returns {AppTypeDefinition}
 */
export function getDefaultAppType() {
  return APP_TYPES_BY_ID[DEFAULT_APP_TYPE_ID];
}

/**
 * App types the user is allowed to PICK when creating a new app in the given environment.
 * Unknown types, disabled types, and types whose createEnvironment doesn't match are excluded.
 * @param {Object} options
 * @param {boolean} options.isDesktop
 * @returns {AppTypeDefinition[]}
 */
export function getSelectableAppTypes({ isDesktop } = {}) {
  return APP_TYPES.filter((t) => {
    if (!t.enabled) return false;
    if (t.createEnvironment === "desktop" && !isDesktop) return false;
    return true;
  });
}

/**
 * Whether the given appType can actually run/open in the current environment.
 * @param {string} appType
 * @param {Object} options
 * @param {boolean} options.isDesktop
 * @returns {boolean}
 */
export function canRunAppTypeInEnvironment(appType, { isDesktop } = {}) {
  const def = getAppType(appType);
  if (!def) return true; // Unknown type: don't block; backward compatible
  if (def.runEnvironment === "desktop" && !isDesktop) return false;
  return true;
}

/**
 * Describe why an app can't be opened in the current environment, or null if it can.
 * @param {Object} app
 * @param {Object} options
 * @param {boolean} options.isDesktop
 * @returns {{ reason: string, requiredEnvironment: string, appType: string }|null}
 */
export function getEnvironmentGateReason(app, { isDesktop } = {}) {
  const appType = normalizeAppType(app?.appType);
  const def = getAppType(appType);
  if (!def) return null;
  if (def.runEnvironment === "desktop" && !isDesktop) {
    return {
      reason: "requires_desktop",
      requiredEnvironment: "desktop",
      appType,
    };
  }
  return null;
}

/**
 * Whether a type supports the Workspace (IDE) experience at all.
 * @param {string} appType
 * @returns {boolean}
 */
export function appTypeSupportsWorkspace(appType) {
  const def = getAppType(appType);
  return def ? def.capabilities.workspace : true;
}

/**
 * Whether a type supports the Playspace (preview) experience at all.
 * @param {string} appType
 * @returns {boolean}
 */
export function appTypeSupportsPlayspace(appType) {
  const def = getAppType(appType);
  return def ? def.capabilities.playspace : true;
}
